import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Plus, Eye, Truck, CheckCircle, Clock, List } from 'lucide-react';
import { format } from 'date-fns';
import api from '../../utils/api';
import StatCard from '../../components/ui/StatCard';
import DataTable from '../../components/ui/DataTable';
import StatusChip from '../../components/ui/StatusChip';
import { useAuth } from '../../context/AuthContext';

const DirectDeliveryList = () => {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [stats, setStats]           = useState({});
  const [loading, setLoading]       = useState(true);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const params = statusFilter ? { status: statusFilter } : {};
    Promise.all([
      api.get('/direct-delivery', { params }),
      api.get('/direct-delivery/stats'),
    ])
      .then(([dRes, sRes]) => {
        setDeliveries(dRes.data.data);
        setStats(sRes.data.data);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [statusFilter]);

  const columns = [
    {
      label: 'Delivery #', key: 'deliveryNumber',
      render: (r) => <span className="font-mono text-zip-red text-xs font-medium">{r.deliveryNumber}</span>,
    },
    { label: 'Supplier', key: 'supplier', render: (r) => r.supplier?.name ?? '—' },
    {
      label: 'Delivered To', key: 'deliveredTo',
      render: (r) => (
        <div>
          <div className="text-zip-text text-sm">{r.deliveredTo?.name}</div>
          <span className={`text-[10px] font-mono ${r.deliveredTo?.type === 'agent' ? 'text-blue-600' : 'text-purple-600'}`}>
            {r.deliveredTo?.type}
          </span>
        </div>
      ),
    },
    {
      label: 'Items', key: 'items',
      render: (r) => <span className="font-mono text-zip-muted">{r.items?.length ?? 0}</span>,
    },
    { label: 'Status', key: 'status', render: (r) => <StatusChip status={r.status} /> },
    {
      label: 'Expected', key: 'expectedDeliveryDate',
      render: (r) => (
        <span className="font-mono text-zip-muted text-xs">
          {r.expectedDeliveryDate ? format(new Date(r.expectedDeliveryDate), 'dd MMM yyyy') : '—'}
        </span>
      ),
    },
    { label: 'Recorded By', key: 'recordedBy', render: (r) => r.recordedBy?.name ?? '—' },
    {
      label: '', key: 'actions',
      render: (r) => (
        <Link
          to={`/direct-delivery/${r._id}`}
          className="inline-flex items-center gap-1.5 text-xs text-zip-muted hover:text-zip-red transition-colors font-mono"
        >
          <Eye size={13} /> View
        </Link>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <StatCard label="Total"      value={stats.total ?? 0}      icon={List} />
        <StatCard label="Initiated"  value={stats.initiated ?? 0}  icon={Clock} />
        <StatCard label="In Transit" value={stats.in_transit ?? 0} icon={Truck} />
        <StatCard label="Delivered"  value={stats.delivered ?? 0}  icon={CheckCircle} />
      </div>

      {/* Filter + action */}
      <div className="flex gap-3 justify-between">
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="zip-input w-44"
        >
          <option value="">All Statuses</option>
          <option value="initiated">Initiated</option>
          <option value="in_transit">In Transit</option>
          <option value="delivered">Delivered</option>
        </select>
        {can('direct_delivery.create') && (
          <button
            onClick={() => navigate('/direct-delivery/new')}
            className="btn-primary flex items-center gap-2 whitespace-nowrap"
          >
            <Plus size={15} /> New Delivery
          </button>
        )}
      </div>

      {/* Table */}
      {loading ? (
        <div className="zip-card p-5 space-y-3">
          {[...Array(5)].map((_, i) => <div key={i} className="h-10 skeleton rounded" />)}
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={deliveries}
          emptyText="No direct deliveries found."
        />
      )}
    </div>
  );
};

export default DirectDeliveryList;
